import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { useCachedTexts } from '../hooks/useCachedTexts';
import Threads from './Threads';
import './MagicTimelineSection.css';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, MorphSVGPlugin);

// Store original path data OUTSIDE component to survive StrictMode double-render
const ORIGINAL_PATH_DATA = {
  '#letter-g': "M320.29 81.76C317.53 82.75 315.92 82.49 314.07 80.14C309.32 74.09 303.04 71.18 295.22 71.43C281.54 71.87 270.39 77.54 261.78 87.92C248.3 104.15 243.59 123.18 245.94 143.81C247.22 155.14 252.65 164.42 263.61 169.47C266.58 170.84 269.66 171.52 272.78 171.86L303.14 156.73C302.62 159.51 302.2 162.32 301.57 165.09C299.5 174.08 294.41 180.84 286.03 184.8C274.34 190.33 262.68 188.53 251.14 184.12C247.44 182.71 246.77 182.96 246.03 186.87C245.04 192.16 244.19 197.46 243.1 202.73C242.63 205.01 243.39 206.16 245.54 206.8C256.84 210.18 268.08 212.59 280.12 210.45C298.02 207.26 312.7 199 324.34 185.23C330.74 177.65 333.99 168.75 335.48 158.94C337.57 145.16 340.33 131.5 342.79 117.79C344.75 106.96 346.65 96.12 348.56 85.29C349.34 80.91 350.03 76.53 350.87 71.5C340.13 75.08 330.15 78.25 320.29 81.76ZM304.67 141.31L285.93 149.65C282.65 148.7 280.26 146.19 279.19 142.26C279.06 141.77 278.95 141.29 278.83 140.8C277.67 135.72 277.57 130.45 278.25 125.28C279.32 117.16 281.16 109.77 285.48 103.18C287.5 100.08 290.33 97.12 293.48 95.19C300.43 90.94 307.85 94.3 309.39 102.3L304.67 141.31Z",
  '#letter-i': "M379.75 147.42C378.94 145.57 378.89 143.3 379.41 140.36C380.2 135.88 381.02 131.43 381.83 126.97L369.32 112.4L351.5 91.66C350.69 101.86 348.67 113.21 347.07 123.35C345.4 134 343.23 144.61 341.82 155.31C341.41 158.43 341.75 161.17 342.72 163.45C344.62 167.91 348.98 170.64 355.09 171.16C355.38 171.18 355.7 171.21 355.99 171.23C358.61 171.39 361.12 171.23 363.57 170.82L383.61 160.82L392.91 155.66L391.8 154.33C384.8 152.53 381.13 150.63 379.75 147.42Z",
  '#star-warped': "M405.36 107.36C399.27 91.55 393.2 75.74 387.01 59.97C386.06 57.55 386.34 56.56 388.97 55.94C393.28 54.94 397.51 53.59 401.77 52.38C406.72 50.97 411.66 49.54 417.75 47.78L383.18 35.43C378.68 33.33 375.15 29.47 373.29 24.77L363.5 0C359.4 7.37 355.63 14 352.03 20.73C350.97 22.72 349.91 23.3 347.74 22.45C343.13 20.64 338.44 19.05 333.78 17.38C329.33 15.79 324.88 14.21 320.17 12.54L338.75 35.94C338.75 35.94 341.79 39.23 340.13 41.73C338.47 44.23 337.11 46.73 337.11 46.73L318.26 77.7L349.78 67.03C350.24 66.86 350.7 66.69 351.16 66.52C356.38 64.54 360.29 64.43 363.75 66.71C364.75 67.37 365.6 68.24 366.37 69.17L389 96.7L386.91 96.11L354.38 87.34L352.41 86.74L358.85 93.61C372.93 110.15 387 126.66 401.08 143.2L403.27 146.04L405.72 144.26C404.46 142.34 403.29 140.37 402.22 138.35C396.44 127.46 390.65 116.56 384.87 105.67L384.21 104.42L385.49 104.98C391.69 107.37 397.96 109.87 404.66 112.45L405.36 107.36Z"
};

const MagicTimelineSection = ({ variant = 'light', milestones = [] }) => {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const svgContainerRef = useRef(null);
  const timelineRef = useRef(null);
  const taglineRef = useRef(null);
  const ctaButtonsRef = useRef(null);
  const overlayRef = useRef(null);
  const isVerifyingRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const { getText } = useCachedTexts('home_magicomms');

  useEffect(() => {
    const section = sectionRef.current;
    const svgContainer = svgContainerRef.current;
    
    if (!section || !svgContainer) return;
    
    // CSS handles initial hidden state via .svg-container { visibility: hidden }
    // We just need to set isReady=true after animation setup to add animation-ready class
    
    // DEBOUNCE: Delay animation setup to let StrictMode's double-render settle
    // This ensures we only set up the animation once, after all renders are done
    const setupTimeoutId = setTimeout(() => {
      setupAnimation();
    }, 50);
    
    // Track scroll handler for cleanup
    let scrollHandler = null;
    
    // Cleanup will clear this timeout if component unmounts quickly (StrictMode)
    const cleanupFn = () => {
      clearTimeout(setupTimeoutId);
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      setIsReady(false);
    };
    
    function setupAnimation() {
    // This fixes the bug where navigating to home doesn't reset the animation properly
    const resetSvgElements = () => {
      const allElements = svgContainer.querySelectorAll('g');
      allElements.forEach(el => {
        // CRITICAL: Remove transform attribute directly - GSAP uses matrix transforms
        el.removeAttribute('transform');
        el.removeAttribute('style');
        gsap.set(el, { clearProps: 'all' });
        const path = el.querySelector('path');
        if (path) {
          path.removeAttribute('style');
          gsap.set(path, { clearProps: 'all' });
        }
      });
      // Also reset the tagline and CTA buttons
      const tagline = taglineRef.current;
      if (tagline) {
        gsap.set(tagline, { clearProps: 'all' });
      }
      const ctaButtons = ctaButtonsRef.current;
      if (ctaButtons) {
        gsap.set(ctaButtons, { clearProps: 'all' });
        gsap.set(ctaButtons, { opacity: 1, y: 0 }); // Ensure buttons are visible after reset
      }
      
      // Explicitly set all letters to visible and in original position
      const letterIds = ['#letter-m-1', '#letter-a', '#letter-g', '#letter-i', '#letter-c', '#letter-o', '#letter-m-2', '#letter-m-3', '#letter-s', '#star-warped'];
      letterIds.forEach(id => {
        const el = svgContainer.querySelector(id);
        if (el) {
          el.removeAttribute('transform');
          el.removeAttribute('style');
          gsap.set(el, { clearProps: 'all' });
          gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1, rotation: 0 });
        }
      });
      
      // CRITICAL: Restore original path data for morphed elements using constant
      Object.keys(ORIGINAL_PATH_DATA).forEach(selector => {
        const el = svgContainer.querySelector(selector);
        if (el) {
          const path = el.querySelector('path');
          if (path && ORIGINAL_PATH_DATA[selector]) {
            path.removeAttribute('style');
            path.setAttribute('d', ORIGINAL_PATH_DATA[selector]);
          }
        }
      });
    };
    
    // Kill any existing ScrollTriggers and timelines first
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    
    // CRITICAL: Pre-reset SVG to original state before any timeline setup
    // This ensures no stale morphed paths are visible during setup
    resetSvgElements();
    console.log('🔄 Pre-setup: SVG reset to original state');
    
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }
    
    // Reset SVG elements immediately
    resetSvgElements();
    
    // Ensure we start at scroll 0
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }

    // CONFIGURATION: Distance the star travels down in Phase 3 (in VH units)
    // Change this SINGLE value to update both the animation and the marker line!
    const STAR_TRAVEL_VH = 125;
    
    let finalYVh = STAR_TRAVEL_VH;
    
    // Try to calculate exact distance if timeline section exists
    const timelineSection = document.querySelector('.timeline-section');
    if (timelineSection) {
      // Calculate distance from viewport center (where star starts phase 3)
      // to the 25% mark of the timeline section
      const sectionRect = section.getBoundingClientRect();
      const timelineRect = timelineSection.getBoundingClientRect();
      
      // 25% down into timeline section
      const targetY = timelineRect.top + (timelineRect.height * 0.25);
      
      // Use calculated value if available
      finalYVh = STAR_TRAVEL_VH;
    } else {
      finalYVh = 125; // Fallback
    }

    // Create main timeline first to calculate duration
    // IMPORTANT: paused: true prevents auto-play, only scroll controls it
    const tl = gsap.timeline({ paused: true });
    console.log('⏸️ Timeline created with paused: true, time:', tl.time());

    // Fade out tagline and CTA buttons at the very start of the animation
    const tagline = taglineRef.current;
    if (tagline) {
      tl.to(tagline, {
        opacity: 0,
        y: -20,
        duration: 0.15,
        ease: "power2.out"
      }, 0);
    }
    const ctaButtons = ctaButtonsRef.current;
    if (ctaButtons) {
      tl.to(ctaButtons, {
        opacity: 0,
        y: -20,
        duration: 0.15,
        ease: "power2.out"
      }, 0);
    }

    // PHASE 1: Spiral collapse and morph (t = 0 → 0.5)
    // This phase implements the black-hole collapse effect
    console.log('🎬 PHASE 1 START: Setting up spiral collapse and morph');
    
    // 1. First, find the center position where we want the final star (use star-warped as reference)
    const starWarpedElement = svgContainer.querySelector('#star-warped');
    let finalCenterX = 400; // fallback
    let finalCenterY = 106; // fallback
    
    if (starWarpedElement) {
      const starBBox = starWarpedElement.getBBox();
      finalCenterX = starBBox.x + starBBox.width / 2;
      finalCenterY = starBBox.y + starBBox.height / 2;
    }
    
    // Create the final star path positioned at the center location
    const starCenterX = 66; // approximate center of the original star path
    const starCenterY = 45; // approximate center of the original star path
    const offsetX = finalCenterX - starCenterX;
    const offsetY = finalCenterY - starCenterY;
    
    const finalStarPath = `M${27.6062 + offsetX} ${75.892 + offsetY}L${42.5117 + offsetX} ${47.096 + offsetY}L${24.031 + offsetX} ${20.439 + offsetY}L${56.096 + offsetX} ${25.7368 + offsetY}L${75.6752 + offsetX} ${-0.0075748 + offsetY}L${80.4939 + offsetX} ${31.9752 + offsetY}L${111.166 + offsetX} ${42.719 + offsetY}L${82.1583 + offsetX} ${57.2334 + offsetY}L${81.4122 + offsetX} ${89.6499 + offsetY}L${58.6588 + offsetX} ${66.542 + offsetY}L${27.6062 + offsetX} ${75.892 + offsetY}Z`;
    
    // 2. Treat G, I, and star-warped as ONE combined element that morphs into one star
    // All three elements morph to the same final star shape simultaneously
    console.log('🌀 PHASE 1: Morphing g, i, and star-warped into final star');
    const morphElements = ['#letter-g', '#letter-i', '#star-warped'];
    
    morphElements.forEach((selector, index) => {
      const element = svgContainer.querySelector(selector);
      if (element) {
        const pathElement = element.querySelector('path');
        if (pathElement) {
          // All three morph to the exact same final star shape at the same time
          tl.to(pathElement, {
            morphSVG: finalStarPath,
            duration: 0.5,
            ease: "power1.inOut"
          }, 0); // All start morphing simultaneously
        }
        
        // After morphing starts, fade out G and I, keep star-warped
        if (selector !== '#star-warped') {
          tl.to(element, {
            opacity: 0,
            duration: 0.001,
            ease: "power2.inOut"
          }, 0.45); // Start fading at progress 0.46, invisible by 0.55
        }
      }
    });

    // PHASE 2: Star twirls to center and grows (t ≈ 0.5 → 0.7)
    // Always go to center first, even on mobile (mobile will curve to left during Phase 3)
    console.log('⭐ PHASE 2 START: Star twirling to center and growing');
    const finalStarElement = svgContainer.querySelector('#star-warped');
    if (finalStarElement) {
      // Get the center of the screen/container
      const containerCenterX = 489.5; // Half of viewBox width (979/2)
      const containerCenterY = 135;   // Half of viewBox height (270/2)
      
      // Calculate movement to screen center
      const currentBBox = finalStarElement.getBBox();
      const currentCenterX = currentBBox.x + currentBBox.width / 2;
      const currentCenterY = currentBBox.y + currentBBox.height / 2;
      const moveToScreenCenterX = containerCenterX - currentCenterX;
      const moveToScreenCenterY = containerCenterY - currentCenterY;
      
      // Use a fixed scale - TimelineSection will measure this and match it exactly
      const finalScale = 1;
      
      // Twirl to center and grow
      console.log(`🌟 PHASE 2: Applying twirl animation - 180° rotation, ${finalScale}x scale`);
      tl.to(finalStarElement, {
        x: `+=${moveToScreenCenterX}`,
        y: `+=${moveToScreenCenterY}`,
        rotation: 180, // Half rotation to end at 180°
        scale: finalScale, // TimelineSection will measure and match this
        transformOrigin: "center center",
        duration: 1.4,
      }, 0.452); // Start after morphing completes
    }

    // Use the same center position for the other letters to converge to
    const targetCenterX = finalCenterX;
    const targetCenterY = finalCenterY;

    // 2. Move letters from left side toward exact center (black-hole pull)
    console.log('⬅️ PHASE 1: Converging left letters (m, a) to center');
    const leftLetters = ['#letter-m-1', '#letter-a'];
    leftLetters.forEach((selector, index) => {
      const element = svgContainer.querySelector(selector);
      if (element) {
        // Get current position of this element
        const elementBBox = element.getBBox();
        const elementCenterX = elementBBox.x + elementBBox.width / 2;
        const elementCenterY = elementBBox.y + elementBBox.height / 2;
        
        // Calculate relative movement needed
        const deltaX = targetCenterX - elementCenterX;
        const deltaY = targetCenterY - elementCenterY;
        
        // Move to center first (without fading out)
        tl.to(element, {
          x: `+=${deltaX}`, // Relative movement to center
          y: `+=${deltaY}`, // Relative movement to center
          scale: 0.1, // Much smaller when they reach center
          transformOrigin: "center center",
          duration: 0.3,
          ease: "power1.out"
        }, 0.1 + (index * 0.05)); // Stagger slightly
        
        // Then fade out before twirling starts
        tl.to(element, {
          scale: 0.05,
          opacity: 0,
          duration: 0.15,
          ease: "power2.in"
        }, 0.3); // Fade out earlier, before twirl
      }
    });

    // 3. Move letters from right side toward exact center
    console.log('➡️ PHASE 1: Converging right letters (c, o, m, m, s) to center');
    const rightLetters = ['#letter-c', '#letter-o', '#letter-m-2', '#letter-m-3', '#letter-s'];
    rightLetters.forEach((selector, index) => {
      const element = svgContainer.querySelector(selector);
      if (element) {
        // Get current position of this element
        const elementBBox = element.getBBox();
        const elementCenterX = elementBBox.x + elementBBox.width / 2;
        const elementCenterY = elementBBox.y + elementBBox.height / 2;
        
        // Calculate relative movement needed
        const deltaX = targetCenterX - elementCenterX;
        const deltaY = targetCenterY - elementCenterY;
        
        // Move to center first (without fading out)
        tl.to(element, {
          x: `+=${deltaX}`, // Relative movement to center
          y: `+=${deltaY}`, // Relative movement to center
          scale: 0.1, // Much smaller when they reach center
          transformOrigin: "center center",
          duration: 0.3,
          ease: "power1.out"
        }, 0.05 + (index * 0.015)); // Much less stagger delay
        
        // Then fade out before twirling starts
        tl.to(element, {
          scale: 0.05,
          opacity: 0,
          duration: 0.15,
          ease: "power1.in"
        }, 0.3); // Fade out earlier, before twirl
      }
    });

    // PHASE 3: Star stays at center - rotation will be controlled by scroll listener
    console.log('🎯 PHASE 3 START: Star rotation controlled by scroll for exact sync');
    // No rotation animation in Phase 3 - we'll control it via scroll event listener below

    // Ensure timeline is at time 0 before ScrollTrigger takes over
    // CRITICAL: Force render at time 0 to set correct initial visual state
    tl.progress(0);
    tl.render(0, true, true); // suppressEvents=true, force=true
    console.log('🔄 Timeline reset to time 0 and forced render before ScrollTrigger setup');

    // Two ScrollTriggers with seamless handoff - no overlap, perfect sync
    console.log('📌 SCROLLTRIGGER 1: Setting up PINNED phase (0 → 1.85s, 300vh)');
    console.log('   Current scroll position:', window.scrollY);
    console.log('   Section position:', section.getBoundingClientRect().top);
    
    // Phase 1 & 2: Pinned animation
    const pinnedTween = gsap.to(tl, {
      time: 1.85,
      ease: 'none',
      paused: true,
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: '+=300vh',
        scrub: true, // Use true instead of 0.5 for smoothest possible scrubbing
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        immediateRender: false,
        toggleActions: 'play none none reset',
        id: 'pinned-phase',
        onUpdate: (self) => {
          // Skip updates during verification to prevent path modifications
          if (isVerifyingRef.current) return;
          const scrolledVh = (self.progress * 300).toFixed(1); // 300vh total for pinned phase
          console.log('Pinned progress:', self.progress.toFixed(3), 'Timeline:', tl.time().toFixed(3), 'Scrolled:', scrolledVh + 'vh / 300vh');
        },
        onEnter: () => console.log('🟢 PINNED: Entered'),
        onLeave: () => {
          if (isVerifyingRef.current) return;
          console.log('🟡 PINNED: Leaving at timeline time:', tl.time());
          // Lock timeline at exactly 1.85 when leaving
          tl.time(1.85);
        },
        onEnterBack: () => {
          if (isVerifyingRef.current) return;
          console.log('🔵 PINNED: Re-entered at timeline time:', tl.time());
        },
        onLeaveBack: () => console.log('⚪ PINNED: Left backwards'),
        onRefresh: () => {
          // During verification, force timeline back to 0 if it drifted
          if (isVerifyingRef.current) {
            tl.time(0);
            tl.pause();
            return;
          }
          console.log('🔄 PINNED: Refreshed, time:', tl.time());
        }
      }
    });

    // Phase 3: Unpinned continuation - starts IMMEDIATELY after pinned phase ends
    console.log('� SCROLLTRIGGER 2: Setting up UNPINNED phase (1.85 → 3.35s)');
    const phase3Anim = gsap.fromTo(tl, 
      { time: 1.85 },
      { 
        time: 3.35,
        duration: 1,
        ease: 'none',
        paused: true
      }
    );
    
    // Wait for timeline-section to exist before creating ScrollTriggers that depend on it
    setTimeout(() => {
      const timelineSectionCheck = document.querySelector('.timeline-section');
      if (!timelineSectionCheck) {
        console.log('⚠️ Timeline section not found, skipping unpinned phase setup');
        return;
      }
      
    const unpinnedST = ScrollTrigger.create({
      trigger: '.timeline-section',
      start: 'top bottom',
      end: 'top+=25% bottom', // End exactly at the 25% marker
      scrub: true, // Match pinned phase - use true for smoothest scrubbing
      id: 'unpinned-phase',
      animation: phase3Anim,
      onUpdate: (self) => {
        // GUARD: Ignore updates when at scroll 0 (initial load race condition)
        if (window.scrollY < 10) {
          return;
        }
        // Calculate vh distance for unpinned phase dynamically
        const startScroll = self.start;
        const endScroll = self.end;
        const scrollDistance = endScroll - startScroll;
        const scrolledDistance = self.progress * scrollDistance;
        const vh = window.innerHeight / 100;
        const scrolledVh = (scrolledDistance / vh).toFixed(1);
        const totalVh = (scrollDistance / vh).toFixed(1);
        const totalFromStart = (300 + parseFloat(scrolledVh)).toFixed(1); // 300vh from pinned + unpinned progress
        
        console.log('Unpinned progress:', self.progress.toFixed(3), 'Timeline:', tl.time().toFixed(3), 
                    'Scrolled:', scrolledVh + 'vh / ' + totalVh + 'vh', 
                    '(Total from start: ' + totalFromStart + 'vh)');
      },
      onEnter: () => {
        // GUARD: Ignore enter events when at scroll 0 (initial load race condition)
        if (window.scrollY < 10) {
          console.log('🟡 UNPINNED: Ignoring false enter at scroll 0');
          return;
        }
        console.log('🟡 UNPINNED: Entered at timeline time:', tl.time());
        // Ensure exact start
        if (Math.abs(tl.time() - 1.85) > 0.001) {
          console.log('   ⚠️ Correcting:', tl.time(), '→ 1.85');
          tl.time(1.85);
        }
      },
      onLeave: () => {
        // GUARD: Ignore leave events when at scroll 0
        if (window.scrollY < 10) {
          return;
        }
        console.log('🔴 UNPINNED: Completed');
      },
      onEnterBack: () => {
        console.log('🔵 UNPINNED: Re-entered at timeline time:', tl.time());
      },
      onLeaveBack: () => {
        console.log('⚪ UNPINNED: Leaving back at timeline time:', tl.time());
        // Lock timeline at exactly 1.85 when leaving back
        tl.time(1.85);
      }
    });
    
    // Disable unpinned ScrollTrigger initially to prevent race condition
    unpinnedST.disable();

    // Visual marker at 25% of completion section height
    const marker25ST = ScrollTrigger.create({
      trigger: '.timeline-section',
      start: 'top+=25% bottom', // 25% down from top of completion section
      end: 'top+=25% bottom',
      id: '25-percent-marker',
      onEnter: () => {
        // Guard against false triggers at scroll 0
        if (window.scrollY < 10) return;
        console.log('📍 Crossed 25% mark of completion section');
      },
      onLeaveBack: () => console.log('📍 Back above 25% mark')
    });
    marker25ST.disable(); // Disable initially

    // Visual marker for the Star's final position
    // Uses the finalYVh config to be perfectly accurate
    const starEndST = ScrollTrigger.create({
      trigger: section,
      // Dynamic start position: Top of section + 50vh (center start) + travel distance
      // Formula: 0.5 (center) + (finalYVh / 100)
      start: () => `top+=${window.innerHeight * (0.5 + (finalYVh / 100))}px center`, 
      end: () => `top+=${window.innerHeight * (0.5 + (finalYVh / 100))}px center`,
      id: 'star-end-marker',
      onEnter: () => console.log(`📍 Reached Star End Position (${finalYVh}vh)`),
      onLeaveBack: () => console.log('📍 Leaving Star End Position')
    });
    starEndST.disable(); // Disable initially
    
    // Enable all secondary ScrollTriggers after a delay to avoid race condition
    setTimeout(() => {
      unpinnedST.enable();
      marker25ST.enable();
      starEndST.enable();
      console.log('✅ All secondary ScrollTriggers enabled after delay');
    }, 500);
    }, 100); // Wait 100ms for timeline section to render

    // Store timeline references for cleanup
    timelineRef.current = tl;

    // Force ScrollTrigger to refresh after everything is set up
    // Add scroll listener to hide star and control rotation during unpinned phase
    const handleScroll = () => {
      // Only control rotation when in unpinned phase (after pinned phase ends)
      const pinnedEnd = pinnedTween.scrollTrigger.end;
      if (window.scrollY > pinnedEnd && finalStarElement) {
        // Hide the MagicTimelineSection star when unpinned phase starts
        gsap.set(finalStarElement, { opacity: 0 });
      } else if (finalStarElement) {
        // Show the star during pinned phase
        gsap.set(finalStarElement, { opacity: 1 });
      }
    };
    
    // Store reference for cleanup
    scrollHandler = handleScroll;
    window.addEventListener('scroll', handleScroll);
    
    // This ensures proper initial positioning
    requestAnimationFrame(() => {
      console.log('🔄 ScrollTrigger refreshing after setup');
      
      // If at scroll 0, disable ScrollTrigger temporarily to set initial state
      if (window.scrollY === 0) {
        console.log('⚠️ At scroll 0, disabling ScrollTrigger to set initial state');
        
        // Get the ScrollTrigger instance
        const st = pinnedTween.scrollTrigger;
        if (st) {
          st.disable(); // Temporarily disable
          console.log('   ScrollTrigger disabled');
        }
        
        // Set initial state - FORCE render at time 0
        tl.progress(0);
        tl.render(0, true, true); // Force immediate render at time 0
        pinnedTween.progress(0);
        pinnedTween.pause();
        
        // CRITICAL: Reset SVG elements to restore original path data after forced render
        resetSvgElements();
        console.log('   Timeline reset to 0, forced render, SVG reset, tween paused');
        
        // Re-enable after a frame
        requestAnimationFrame(() => {
          if (st) {
            st.enable(); // Re-enable
            st.refresh(); // Refresh with correct initial state
            console.log('   ScrollTrigger re-enabled and refreshed');
          }
          console.log('✅ Final state - Timeline time:', tl.time(), 'Scroll:', window.scrollY);
          
          // SVG is always visible now - just ensure correct state
          resetSvgElements();
          tl.progress(0);
          tl.pause();
          
          // Verify BOTH paths AND transforms are in correct state before showing
          // Uses triple-verification to ensure state is stable (GSAP may modify async)
          // Set verifying flag to prevent ScrollTrigger callbacks from modifying timeline
          isVerifyingRef.current = true;
          let verificationPassCount = 0;
          
          const verifyState = () => {
            let allCorrect = true;
            
            // 1. Check path data
            Object.keys(ORIGINAL_PATH_DATA).forEach(selector => {
              const el = svgContainer.querySelector(selector);
              if (el) {
                const path = el.querySelector('path');
                if (path) {
                  const currentD = path.getAttribute('d');
                  if (currentD !== ORIGINAL_PATH_DATA[selector]) {
                    console.log('⚠️ Path mismatch for', selector, '- restoring');
                    path.setAttribute('d', ORIGINAL_PATH_DATA[selector]);
                    allCorrect = false;
                  }
                }
              }
            });
            
            // 2. Check GSAP transforms on all letter elements
            // CRITICAL: Also check the DOM transform attribute directly - GSAP uses matrix transforms
            // that bypass individual property checks (x, y, scale, rotation)
            const letterIds = ['#letter-m-1', '#letter-a', '#letter-g', '#letter-i', '#letter-c', '#letter-o', '#letter-m-2', '#letter-m-3', '#letter-s', '#star-warped'];
            letterIds.forEach(id => {
              const el = svgContainer.querySelector(id);
              if (el) {
                // Check if element has a non-identity transform attribute
                // Identity matrix matrix(1,0,0,1,0,0) is OK - it means no transform
                const transformAttr = el.getAttribute('transform');
                const isIdentityMatrix = !transformAttr || transformAttr === '' || transformAttr === 'matrix(1,0,0,1,0,0)';
                if (!isIdentityMatrix) {
                  console.log('⚠️ Non-identity transform found for', id, ':', transformAttr, '- clearing');
                  el.removeAttribute('transform');
                  gsap.set(el, { clearProps: 'all' });
                  gsap.set(el, { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 });
                  allCorrect = false;
                }
                
                // Also check GSAP's cached transform values
                const x = gsap.getProperty(el, 'x') || 0;
                const y = gsap.getProperty(el, 'y') || 0;
                const scale = gsap.getProperty(el, 'scale') || 1;
                const rotation = gsap.getProperty(el, 'rotation') || 0;
                const opacity = gsap.getProperty(el, 'opacity');
                
                // Check if any transform is not at initial state
                if (Math.abs(x) > 0.1 || Math.abs(y) > 0.1 || Math.abs(scale - 1) > 0.01 || Math.abs(rotation) > 0.1 || (opacity !== undefined && opacity < 0.99)) {
                  console.log('⚠️ Transform mismatch for', id, '- x:', x, 'y:', y, 'scale:', scale, 'rotation:', rotation, 'opacity:', opacity);
                  el.removeAttribute('transform');
                  gsap.set(el, { clearProps: 'all' });
                  gsap.set(el, { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 });
                  allCorrect = false;
                }
              }
            });
            
            return allCorrect;
          };
          
          const verifyAndShow = () => {
            const isCorrect = verifyState();
            
            if (!isCorrect) {
              // Something was wrong, reset verification count and try again
              verificationPassCount = 0;
              resetSvgElements();
              tl.progress(0);
              tl.pause();
              requestAnimationFrame(verifyAndShow);
              return;
            }
            
            // Increment pass count
            verificationPassCount++;
            
            // Need 3 consecutive passes to ensure state is truly stable
            // This catches async GSAP modifications that happen between frames
            if (verificationPassCount < 3) {
              console.log(`🔍 Verification pass ${verificationPassCount}/3`);
              requestAnimationFrame(verifyAndShow);
              return;
            }
            
            // Force layout synchronization before showing
            svgContainer.offsetHeight; // Force reflow
            
            // All verified correct across 3 RAF frames
            // BUT: TimelineSection loading may trigger layout changes AFTER these 3 frames
            // Add a delayed verification to catch post-render layout changes
            console.log('✅ Animation ready, SVG verified stable across 3 frames');
            console.log('🔄 Starting delayed verification to catch post-render layout changes...');
            
            setTimeout(() => {
              // Final verification after potential layout changes
              const finalCheck = verifyState();
              if (!finalCheck) {
                console.log('⚠️ Delayed verification caught issue, restarting verification...');
                verificationPassCount = 0;
                resetSvgElements();
                tl.progress(0);
                tl.pause();
                requestAnimationFrame(verifyAndShow);
                return;
              }
              
              // All verified - safe to show
              isVerifyingRef.current = false;
              
              // Ensure timeline is at exactly 0 and paused before showing
              tl.progress(0);
              tl.pause();
              
              // Set tagline and buttons to visible state
              const tagline = taglineRef.current;
              if (tagline) {
                gsap.set(tagline, { opacity: 1, y: 0 });
              }
              const ctaButtons = ctaButtonsRef.current;
              if (ctaButtons) {
                gsap.set(ctaButtons, { opacity: 1, y: 0 });
              }
              
              // Set ready state (triggers button visibility via CSS)
              setIsReady(true);
              console.log('✅ Delayed verification passed, SVG shown');
              
              // Fade OUT the white overlay to reveal content (avoids WebGL compositing issues)
              const overlay = overlayRef.current;
              if (overlay) {
                gsap.to(overlay, { 
                  opacity: 0, 
                  duration: 0.5, 
                  ease: "power2.out",
                  onComplete: () => {
                    overlay.style.display = 'none'; // Remove from rendering
                  }
                });
              }
              
              // CRITICAL: React StrictMode causes double-mount, so a SECOND data fetch
              // may arrive AFTER we show the SVG. Add post-show monitoring to catch this.
              // Run silent fixes for 500ms after showing to catch any late modifications.
              let postShowChecks = 0;
              const maxPostShowChecks = 10; // 10 checks over 500ms
              const svgShownAt = Date.now();
              console.log('🔍 Post-show monitoring started at', svgShownAt);
              
              const postShowMonitor = () => {
                if (postShowChecks >= maxPostShowChecks) {
                  console.log('✅ Post-show monitoring complete after', Date.now() - svgShownAt, 'ms');
                  return;
                }
                postShowChecks++;
                
                // Silently fix any path mismatches without hiding/showing
                let hadIssue = false;
                const issueDetails = [];
                
                Object.keys(ORIGINAL_PATH_DATA).forEach(selector => {
                  const el = svgContainer.querySelector(selector);
                  if (el) {
                    const path = el.querySelector('path');
                    if (path && path.getAttribute('d') !== ORIGINAL_PATH_DATA[selector]) {
                      issueDetails.push(selector);
                      path.setAttribute('d', ORIGINAL_PATH_DATA[selector]);
                      hadIssue = true;
                    }
                    // Also check and fix transforms
                    const transformAttr = el.getAttribute('transform');
                    if (transformAttr && transformAttr !== '' && transformAttr !== 'matrix(1,0,0,1,0,0)') {
                      issueDetails.push(`${selector}[transform=${transformAttr}]`);
                      el.removeAttribute('transform');
                      hadIssue = true;
                    }
                  }
                });
                
                if (hadIssue) {
                  console.log('🔧 Post-show fix #' + postShowChecks + ' at +' + (Date.now() - svgShownAt) + 'ms:', issueDetails.join(', '));
                  // Reset timeline to ensure correct state
                  tl.progress(0);
                  tl.pause();
                  gsap.set(['#letter-m-1', '#letter-a', '#letter-g', '#letter-i', '#letter-c', '#letter-o', '#letter-m-2', '#letter-m-3', '#letter-s', '#star-warped'].map(id => svgContainer.querySelector(id)).filter(Boolean), {
                    clearProps: 'all'
                  });
                }
                
                // Continue monitoring
                setTimeout(postShowMonitor, 50);
              };
              
              // Start post-show monitoring
              postShowMonitor();
            }, 150); // Wait 150ms for TimelineSection data fetch + render
          };
          
          // Start verification
          requestAnimationFrame(verifyAndShow);
        });
      } else {
        ScrollTrigger.refresh();
        console.log('✅ Final state - Timeline time:', tl.time(), 'Scroll:', window.scrollY);
        
        // SVG is always visible now - just ensure correct state
        resetSvgElements();
        tl.progress(0);
        tl.pause();
        
        // Verify BOTH paths AND transforms are in correct state before showing
        // Uses triple-verification to ensure state is stable (GSAP may modify async)
        // Set verifying flag to prevent ScrollTrigger callbacks from modifying timeline
        isVerifyingRef.current = true;
        let verificationPassCount = 0;
        
        const verifyState = () => {
          let allCorrect = true;
          
          // 1. Check path data
          Object.keys(ORIGINAL_PATH_DATA).forEach(selector => {
            const el = svgContainer.querySelector(selector);
            if (el) {
              const path = el.querySelector('path');
              if (path) {
                const currentD = path.getAttribute('d');
                if (currentD !== ORIGINAL_PATH_DATA[selector]) {
                  console.log('⚠️ Path mismatch for', selector, '- restoring');
                  path.setAttribute('d', ORIGINAL_PATH_DATA[selector]);
                  allCorrect = false;
                }
              }
            }
          });
          
          // 2. Check GSAP transforms on all letter elements
          // CRITICAL: Also check the DOM transform attribute directly - GSAP uses matrix transforms
          // that bypass individual property checks (x, y, scale, rotation)
          const letterIds = ['#letter-m-1', '#letter-a', '#letter-g', '#letter-i', '#letter-c', '#letter-o', '#letter-m-2', '#letter-m-3', '#letter-s', '#star-warped'];
          letterIds.forEach(id => {
            const el = svgContainer.querySelector(id);
            if (el) {
              // Check if element has a non-identity transform attribute
              // Identity matrix matrix(1,0,0,1,0,0) is OK - it means no transform
              const transformAttr = el.getAttribute('transform');
              const isIdentityMatrix = !transformAttr || transformAttr === '' || transformAttr === 'matrix(1,0,0,1,0,0)';
              if (!isIdentityMatrix) {
                console.log('⚠️ Non-identity transform found for', id, ':', transformAttr, '- clearing');
                el.removeAttribute('transform');
                gsap.set(el, { clearProps: 'all' });
                gsap.set(el, { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 });
                allCorrect = false;
              }
              
              // Also check GSAP's cached transform values
              const x = gsap.getProperty(el, 'x') || 0;
              const y = gsap.getProperty(el, 'y') || 0;
              const scale = gsap.getProperty(el, 'scale') || 1;
              const rotation = gsap.getProperty(el, 'rotation') || 0;
              const opacity = gsap.getProperty(el, 'opacity');
              
              // Check if any transform is not at initial state
              if (Math.abs(x) > 0.1 || Math.abs(y) > 0.1 || Math.abs(scale - 1) > 0.01 || Math.abs(rotation) > 0.1 || (opacity !== undefined && opacity < 0.99)) {
                console.log('⚠️ Transform mismatch for', id, '- x:', x, 'y:', y, 'scale:', scale, 'rotation:', rotation, 'opacity:', opacity);
                el.removeAttribute('transform');
                gsap.set(el, { clearProps: 'all' });
                gsap.set(el, { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 });
                allCorrect = false;
              }
            }
          });
          
          return allCorrect;
        };
        
        const verifyAndShow = () => {
          const isCorrect = verifyState();
          
          if (!isCorrect) {
            // Something was wrong, reset verification count and try again
            verificationPassCount = 0;
            resetSvgElements();
            tl.progress(0);
            tl.pause();
            requestAnimationFrame(verifyAndShow);
            return;
          }
          
          // Increment pass count
          verificationPassCount++;
          
          // Need 3 consecutive passes to ensure state is truly stable
          // This catches async GSAP modifications that happen between frames
          if (verificationPassCount < 3) {
            console.log(`🔍 Verification pass ${verificationPassCount}/3`);
            requestAnimationFrame(verifyAndShow);
            return;
          }
          
          // Force layout synchronization before showing
          svgContainer.offsetHeight; // Force reflow
          
          // All verified correct across 3 RAF frames
          // BUT: TimelineSection loading may trigger layout changes AFTER these 3 frames
          // Add a delayed verification to catch post-render layout changes
          console.log('✅ Animation ready, SVG verified stable across 3 frames');
          console.log('🔄 Starting delayed verification to catch post-render layout changes...');
          
          setTimeout(() => {
            // Final verification after potential layout changes
            const finalCheck = verifyState();
            if (!finalCheck) {
              console.log('⚠️ Delayed verification caught issue, restarting verification...');
              verificationPassCount = 0;
              resetSvgElements();
              tl.progress(0);
              tl.pause();
              requestAnimationFrame(verifyAndShow);
              return;
            }
            
            // All verified - safe to show
            isVerifyingRef.current = false;
            
            // Ensure timeline is at exactly 0 and paused before showing
            tl.progress(0);
            tl.pause();
            
            // Set tagline and buttons to visible state
            const tagline = taglineRef.current;
            if (tagline) {
              gsap.set(tagline, { opacity: 1, y: 0 });
            }
            const ctaButtons = ctaButtonsRef.current;
            if (ctaButtons) {
              gsap.set(ctaButtons, { opacity: 1, y: 0 });
            }
            
            // Set ready state (triggers button visibility via CSS)
            setIsReady(true);
            console.log('✅ Delayed verification passed, SVG shown');
            
            // Fade OUT the white overlay to reveal content (avoids WebGL compositing issues)
            const overlay = overlayRef.current;
            if (overlay) {
              gsap.to(overlay, { 
                opacity: 0, 
                duration: 0.5, 
                ease: "power2.out",
                onComplete: () => {
                  overlay.style.display = 'none'; // Remove from rendering
                }
              });
            }
            
            // CRITICAL: React StrictMode causes double-mount, so a SECOND data fetch
            // may arrive AFTER we show the SVG. Add post-show monitoring to catch this.
            // Run silent fixes for 500ms after showing to catch any late modifications.
            let postShowChecks = 0;
            const maxPostShowChecks = 10; // 10 checks over 500ms
            const svgShownAt = Date.now();
            console.log('🔍 Post-show monitoring started at', svgShownAt);
            
            const postShowMonitor = () => {
              if (postShowChecks >= maxPostShowChecks) {
                console.log('✅ Post-show monitoring complete after', Date.now() - svgShownAt, 'ms');
                return;
              }
              postShowChecks++;
              
              // Silently fix any path mismatches without hiding/showing
              let hadIssue = false;
              const issueDetails = [];
              
              Object.keys(ORIGINAL_PATH_DATA).forEach(selector => {
                const el = svgContainer.querySelector(selector);
                if (el) {
                  const path = el.querySelector('path');
                  if (path && path.getAttribute('d') !== ORIGINAL_PATH_DATA[selector]) {
                    issueDetails.push(selector);
                    path.setAttribute('d', ORIGINAL_PATH_DATA[selector]);
                    hadIssue = true;
                  }
                  // Also check and fix transforms
                  const transformAttr = el.getAttribute('transform');
                  if (transformAttr && transformAttr !== '' && transformAttr !== 'matrix(1,0,0,1,0,0)') {
                    issueDetails.push(`${selector}[transform=${transformAttr}]`);
                    el.removeAttribute('transform');
                    hadIssue = true;
                  }
                }
              });
              
              if (hadIssue) {
                console.log('🔧 Post-show fix #' + postShowChecks + ' at +' + (Date.now() - svgShownAt) + 'ms:', issueDetails.join(', '));
                // Reset timeline to ensure correct state
                tl.progress(0);
                tl.pause();
                gsap.set(['#letter-m-1', '#letter-a', '#letter-g', '#letter-i', '#letter-c', '#letter-o', '#letter-m-2', '#letter-m-3', '#letter-s', '#star-warped'].map(id => svgContainer.querySelector(id)).filter(Boolean), {
                  clearProps: 'all'
                });
              }
              
              // Continue monitoring
              setTimeout(postShowMonitor, 50);
            };
            
            // Start post-show monitoring
            postShowMonitor();
          }, 150); // Wait 150ms for TimelineSection data fetch + render
        };
        
        // Start verification
        requestAnimationFrame(verifyAndShow);
      }
    });
    } // End of setupAnimation function

    // Return cleanup function
    return cleanupFn;
  }, []); // Empty dependency array - only run once on mount

  // Choose SVG content based on variant
  // Since background is now plain (no ColorBends), use black fill for visibility
  const fillColor = '#1e1e1c';

  return (
    <section 
      id="magic-timeline" 
      className={`magic-timeline-section ${variant}${isReady ? ' animation-ready' : ''}`}
      ref={sectionRef}
    >
      {/* White overlay that fades out to reveal content */}
      <div className="reveal-overlay" ref={overlayRef}></div>
      
      {/* Threads background effect */}
      <div className="threads-background">
        <Threads
          color={[0.3, 0.3, 0.3]}
          amplitude={1.2}
          distance={0.1}
          yOffset={0.}
          enableMouseInteraction={false}
        />
      </div>
            
      <div 
        className={`svg-container ${isReady ? 'ready' : ''}`} 
        ref={svgContainerRef}
      >
        <svg 
          width="979" 
          height="270" 
          viewBox="0 0 979 270" 
          className="logo-svg"
        >
          {/* Tagline below logo */}
          <text 
            ref={taglineRef}
            x="489.5" 
            y="245" 
            textAnchor="middle" 
            className="logo-tagline"
            fill={fillColor}
          >
            PR, MEDIA RELATIONS & DIGITAL CONTENT
          </text>
          {/* Inline the logo letters SVG content */}
          <g id="letter-g">
            <path d="M320.29 81.76C317.53 82.75 315.92 82.49 314.07 80.14C309.32 74.09 303.04 71.18 295.22 71.43C281.54 71.87 270.39 77.54 261.78 87.92C248.3 104.15 243.59 123.18 245.94 143.81C247.22 155.14 252.65 164.42 263.61 169.47C266.58 170.84 269.66 171.52 272.78 171.86L303.14 156.73C302.62 159.51 302.2 162.32 301.57 165.09C299.5 174.08 294.41 180.84 286.03 184.8C274.34 190.33 262.68 188.53 251.14 184.12C247.44 182.71 246.77 182.96 246.03 186.87C245.04 192.16 244.19 197.46 243.1 202.73C242.63 205.01 243.39 206.16 245.54 206.8C256.84 210.18 268.08 212.59 280.12 210.45C298.02 207.26 312.7 199 324.34 185.23C330.74 177.65 333.99 168.75 335.48 158.94C337.57 145.16 340.33 131.5 342.79 117.79C344.75 106.96 346.65 96.12 348.56 85.29C349.34 80.91 350.03 76.53 350.87 71.5C340.13 75.08 330.15 78.25 320.29 81.76ZM304.67 141.31L285.93 149.65C282.65 148.7 280.26 146.19 279.19 142.26C279.06 141.77 278.95 141.29 278.83 140.8C277.67 135.72 277.57 130.45 278.25 125.28C279.32 117.16 281.16 109.77 285.48 103.18C287.5 100.08 290.33 97.12 293.48 95.19C300.43 90.94 307.85 94.3 309.39 102.3L304.67 141.31Z" fill={fillColor}/>
          </g>
          <g id="letter-a">
            <path d="M235.6 144.38C235.66 142.31 235.96 140.24 236.31 138.19L247.16 70.1201C236.41 73.6901 226.43 76.8801 216.57 80.3901C213.81 81.3801 212.2 81.1201 210.35 78.7601C205.6 72.7301 199.32 69.8001 191.5 70.0601C177.82 70.5001 166.67 76.1701 158.05 86.5401C144.58 102.77 139.88 121.8 142.22 142.45C143.5 153.78 148.93 163.06 159.89 168.11C162.86 169.47 165.94 170.15 169.06 170.49L199.42 155.36L200.66 154.84C200.79 156.25 200.85 157.22 200.92 158.12C201.36 165.07 205.45 169.42 212.38 170.17C218.6 170.85 224.51 169.75 230.12 166.89C236.12 163.82 241.49 159.99 246.47 154.63C244.92 154.31 244.01 154.1 243.11 153.9C237.57 152.67 235.42 150 235.6 144.38ZM200.95 139.92L182.21 148.28C178.93 147.33 176.54 144.8 175.47 140.89C175.34 140.4 175.23 139.92 175.11 139.43C173.95 134.34 173.85 129.07 174.53 123.91C175.6 115.78 177.44 108.4 181.76 101.79C183.78 98.6901 186.61 95.7301 189.75 93.8201C196.72 89.5701 204.14 92.9101 205.68 100.92L200.95 139.92Z" fill={fillColor}/>
          </g>
          <g id="letter-o">
            <path d="M583.94 102.58C582.68 92.81 578.36 84.36 570 78.51C560.9 72.16 550.57 70.81 539.86 71.56C525.87 72.55 513.83 77.93 504.31 88.37C490.92 103.03 485.72 120.51 487.3 140.08C488 148.62 491.36 156.18 497.62 162.18C505.83 170.07 516 172.41 527.74 172.2C536.1 172.41 544.76 170.52 552.82 166.27C576.1 153.98 586.95 126.03 583.94 102.58ZM541.18 144.8C540.03 146.3 538.33 147.48 536.69 148.47C530.16 152.35 522.72 149.44 520.73 142.16C520.6 141.67 520.47 141.19 520.36 140.7C519.21 135.64 519.13 130.4 519.83 125.28C520.93 117.12 522.85 109.76 527.02 103.05C528.49 100.71 530.37 98.49 532.47 96.71C539.62 90.71 548.85 93.25 550.68 102.3C553.77 117.67 550.94 132.14 541.18 144.8Z" fill={fillColor}/>
          </g>
          <g id="letter-m-1">
            <path d="M36.58 48.34C37.8 44.64 39.8 45.67 45.02 45.57C49.3 45.49 53.59 45.61 57.88 45.55C60.24 45.52 61.21 46.47 61.74 48.87C66.26 69.27 70.94 89.63 75.58 110C76.28 113.06 76.98 116.12 77.68 119.18L77.72 119.42H78.9C82.17 113.89 85.47 108.38 88.69 102.82C98.99 85.07 109.3 67.33 119.48 49.51C121.13 46.63 123.04 45.3 126.46 45.48C131.71 45.75 136.99 45.65 142.25 45.52C145.02 45.45 146.27 46.19 145.96 49.27C144.04 68.26 142.22 87.26 140.41 106.27C139.1 120.04 137.8 133.8 136.59 147.58C135.92 155.22 136.42 162.72 141.18 169.92C125.03 169.92 109.59 169.92 93.39 169.92C103.47 156.21 101.92 140.48 103.61 125.38C103.17 125.2 102.73 125.02 102.29 124.84C101.51 126.08 100.69 127.29 99.96 128.55C92.51 141.46 85.04 154.36 77.68 167.32C76.52 169.37 75.19 170.37 72.77 170.31C67.02 170.18 61.26 170.19 55.51 170.3C53.3 170.34 52.29 169.64 51.78 167.38C48.83 154.15 45.71 140.95 42.65 127.74C42.44 126.85 42.19 125.97 41.95 125.08C41.52 125.01 41.09 124.93 40.66 124.86C38.64 132.26 36.55 139.63 34.63 147.06C32.68 154.62 31.71 162.2 35.71 169.97C23.44 169.97 11.78 169.97 0 169.97C0.08 169.42 2.98023e-07 168.96 0.18 168.76C8.57 159.89 10.95 148.44 13.83 137.19C17.32 123.58 20.84 109.99 24.34 96.39C27.75 83.14 31.16 69.9 34.58 56.65C34.65 56.38 34.73 56.11 34.81 55.84L36.58 48.34Z" fill={fillColor}/>
          </g>
          <g id="letter-m-3">
            <path d="M875.74 146.31C875.76 144.97 875.78 143.6 876.02 142.28C877.86 132.18 879.83 122.1 881.61 111.99C883.78 99.6699 883.19 87.9999 872.99 78.8999C866.07 72.7299 857.79 71.0299 848.78 71.4999C842.15 71.8399 839.51 77.2399 835.74 81.2399C834.74 82.2999 833.92 83.54 832.97 84.66C831.66 86.2 830.31 87.6999 828.84 89.3699C828.07 88.4499 827.5 87.9699 827.17 87.3499C820.78 75.3399 810.36 70.9999 797.31 71.4599C793.33 71.5999 790.25 72.9699 788.09 76.4199C786.4 79.1199 784.51 81.6999 782.71 84.3299C782.38 84.2099 782.05 84.09 781.71 83.97C782.27 80.11 782.84 76.2399 783.52 71.5499C779.82 72.7699 777.06 73.6799 774.31 74.5899C764.34 77.8699 754.37 81.15 744.39 84.41C742.31 85.09 741.74 86.1199 742.84 88.2499C746.11 94.5499 745.79 101.3 744.69 107.99C742.82 119.32 740.71 130.62 738.72 141.93C737.04 151.5 734.96 160.91 728.33 168.56C728.08 168.85 728.12 169.4 728 169.94C742.95 169.94 757.68 169.94 772.75 169.94C772.3 168.65 772.04 167.73 771.67 166.86C769.61 162.03 768.71 157 769.59 151.81C772.1 136.98 774.53 122.14 777.4 107.38C778.04 104.09 779.72 100.52 782.02 98.1299C786.99 92.9499 795.13 94.1599 797.2 100.11C797.99 102.38 797.72 105.19 797.33 107.66C796.04 115.87 794.65 124.07 792.96 132.21C791.11 141.16 789.23 150.13 786.68 158.9C785.57 162.72 782.75 166.04 780.51 169.93C795.55 169.93 810.33 169.93 824.26 169.93C823.51 163.95 821.68 158.11 822.3 152.54C823.72 139.68 826.26 126.93 828.49 114.16C829.2 110.09 829.96 105.95 831.38 102.09C833.31 96.8399 840.05 93.8099 844.97 95.4199C849 96.7399 851 101.13 850.04 106.35C847.63 119.45 845.19 132.55 842.92 145.68C842.09 150.49 841.25 155.36 841.21 160.21C841.16 166.7 845.63 171.14 852.06 171.96C859.1 172.86 865.7 171.41 871.85 168.01C877.28 165.02 882.35 161.53 886.8 156.38C885.14 156.07 884.09 155.91 883.05 155.66C877.71 154.43 875.64 151.77 875.74 146.31Z" fill={fillColor}/>
          </g>
          <g id="letter-m-2">
            <path d="M731.03 156.3C730.57 156.17 730.12 155.99 729.65 155.91C722.27 154.54 720.32 152.09 720.83 144.65C720.96 142.83 721.35 141.02 721.65 139.22C723.59 127.53 726.18 115.91 727.34 104.14C728.95 87.82 720.43 76.12 704.56 72.22C696.47 70.23 688.48 70.42 682.9 78.51C680.29 82.29 677.1 85.68 674 89.44C673.17 88.48 672.49 87.93 672.1 87.22C665.57 75.07 654.96 70.9 641.8 71.5C638.07 71.67 635.25 73.1 633.23 76.26C631.49 78.98 629.62 81.62 627.8 84.29C627.47 84.2 627.14 84.11 626.8 84.02C627.33 80.09 627.86 76.16 628.48 71.56C614.74 76.07 602 80.26 589.26 84.45C587.41 85.06 586.66 86.03 587.72 88.02C591.14 94.42 590.8 101.27 589.67 108.09C587.81 119.3 586.04 130.55 583.65 141.65C582.12 148.76 579.75 155.71 577.35 162.59C576.47 165.1 574.4 167.2 572.59 169.9C587.94 169.9 602.71 169.9 618 169.9C614.49 162.75 613.71 155.84 615.12 148.46C617.72 134.9 619.83 121.24 622.23 107.64C623.11 102.66 624.99 98.31 630.02 95.96C635.92 93.21 642.35 96.28 642.76 102.3C642.88 104.11 642.66 105.97 642.35 107.77C640.41 118.97 638.69 130.22 636.29 141.32C634.73 148.55 632.35 155.62 629.92 162.62C629.05 165.13 626.94 167.22 625.13 169.9C640.44 169.9 655.21 169.9 670.36 169.9C669.88 168.56 669.42 167.69 669.27 166.77C668.49 162 666.72 157.09 667.27 152.47C668.81 139.62 671.26 126.87 673.49 114.1C674.2 110.03 674.95 105.88 676.38 102.03C678.32 96.8 685.14 93.77 690 95.41C693.9 96.73 695.98 101.03 695.08 106.02C692.69 119.25 690.22 132.47 687.92 145.71C687.1 150.4 686.26 155.14 686.16 159.87C686.03 166.53 690.5 171.23 697.08 171.92C711.17 173.41 721.67 166.54 731 157.08C731.13 156.97 731.03 156.63 731.03 156.3Z" fill={fillColor}/>
          </g>
          <g id="letter-c">
            <path d="M468.11 91.74C474.47 92.73 479.32 96.52 483.69 100.98C485.01 102.33 486.24 103.76 487.73 105.39C488.01 104.45 488.22 103.93 488.31 103.39C489.68 95.7301 490.95 88.06 492.43 80.43C493.31 75.88 493.44 75.49 488.87 74.43C483.19 73.12 477.42 71.9101 471.62 71.4001C453.24 69.7901 436.71 74.3901 424.12 88.4501C410.22 103.97 404.68 122.43 408.02 143.12C410.28 157.12 418.56 166.63 432.16 170.35C445.99 174.14 459.93 172.71 473.65 168.63C476.47 167.79 477.44 166.11 477.88 163.55C479.1 156.36 480.41 149.18 481.62 141.99C481.79 140.96 481.64 139.87 481.64 138.35C480.51 139.26 479.85 139.79 479.19 140.32C473.15 145.13 466.91 149.67 459.23 151.43C452.5 152.97 445.76 153.17 440.24 147.98C435.8 143.8 434.63 138.14 434.56 132.43C434.44 123.09 436.49 114.14 440.76 105.77C446.38 94.8101 455.95 89.85 468.11 91.74Z" fill={fillColor}/>
          </g>
          <g id="letter-s">
            <path d="M972.22 74.13C958.46 71.82 944.64 69.94 930.67 72.88C920.2 75.08 910.86 79.37 903.99 87.74C892.94 101.21 893.1 119.26 910.5 128.82C915.63 131.64 920.98 134.05 926.16 136.78C929.06 138.31 932.06 139.81 934.58 141.86C937.36 144.13 936.58 147.63 933.15 148.64C931.24 149.21 928.95 149.37 927.05 148.87C919.63 146.91 912.25 144.74 904.95 142.39C901.07 141.14 897.36 139.37 892.96 137.57C891.23 147.59 889.55 156.95 888.13 166.35C888.02 167.1 889.63 168.72 890.65 168.94C906.21 172.16 921.98 173.49 937.72 171.23C950.45 169.41 961.39 163.81 967.09 151.33C973.22 137.91 972.61 125.64 960.25 117.12C957.05 114.91 953.52 113.16 950.05 111.37C945.71 109.13 941.21 107.21 936.92 104.88C934.6 103.62 932.78 101.64 933.55 98.61C934.38 95.34 937.08 94.49 939.87 94.85C944.56 95.46 949.34 96.1 953.8 97.58C959.84 99.58 965.69 102.18 971.83 104.71C972.9 105.15 974.11 104.58 974.48 103.49C974.65 103 974.8 102.51 974.9 102.01C976.18 95.78 977.47 89.56 978.6 83.31C979.99 75.74 979.97 75.43 972.22 74.13Z" fill={fillColor}/>
          </g>
          <g id="letter-i">
            <path d="M379.75 147.42C378.94 145.57 378.89 143.3 379.41 140.36C380.2 135.88 381.02 131.43 381.83 126.97L369.32 112.4L351.5 91.66C350.69 101.86 348.67 113.21 347.07 123.35C345.4 134 343.23 144.61 341.82 155.31C341.41 158.43 341.75 161.17 342.72 163.45C344.62 167.91 348.98 170.64 355.09 171.16C355.38 171.18 355.7 171.21 355.99 171.23C358.61 171.39 361.12 171.23 363.57 170.82L383.61 160.82L392.91 155.66L391.8 154.33C384.8 152.53 381.13 150.63 379.75 147.42Z" fill={fillColor}/>
          </g>
          <g id="star-warped">
            <path d="M405.36 107.36C399.27 91.55 393.2 75.74 387.01 59.97C386.06 57.55 386.34 56.56 388.97 55.94C393.28 54.94 397.51 53.59 401.77 52.38C406.72 50.97 411.66 49.54 417.75 47.78L383.18 35.43C378.68 33.33 375.15 29.47 373.29 24.77L363.5 0C359.4 7.37 355.63 14 352.03 20.73C350.97 22.72 349.91 23.3 347.74 22.45C343.13 20.64 338.44 19.05 333.78 17.38C329.33 15.79 324.88 14.21 320.17 12.54L338.75 35.94C338.75 35.94 341.79 39.23 340.13 41.73C338.47 44.23 337.11 46.73 337.11 46.73L318.26 77.7L349.78 67.03C350.24 66.86 350.7 66.69 351.16 66.52C356.38 64.54 360.29 64.43 363.75 66.71C364.75 67.37 365.6 68.24 366.37 69.17L389 96.7L386.91 96.11L354.38 87.34L352.41 86.74L358.85 93.61C372.93 110.15 387 126.66 401.08 143.2L403.27 146.04L405.72 144.26C404.46 142.34 403.29 140.37 402.22 138.35C396.44 127.46 390.65 116.56 384.87 105.67L384.21 104.42L385.49 104.98C391.69 107.37 397.96 109.87 404.66 112.45L405.36 107.36Z" fill={fillColor}/>
          </g>
        </svg>
      </div>
      
      {/* CTA Buttons - positioned absolutely relative to section */}
      <div className="hero-cta-buttons" ref={ctaButtonsRef}>
        <Link to="/about" className="hero-cta-button hero-cta-primary">
          {getText('about_button', t('hero.aboutButton'))}
        </Link>
        <Link to="/about#services" className="hero-cta-button hero-cta-secondary">
          {getText('services_button', t('hero.servicesButton'))}
        </Link>
      </div>
    </section>
  );
};

export default MagicTimelineSection;
